import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Plus, Trash2, Loader2, BookOpen, Pencil, Check, Sparkles, Lock,
  UploadCloud, AlertTriangle, X, Users, Ban, RotateCcw,
} from 'lucide-react';
import { Button } from '@easyflow/ui-frontend/components/ui/button';
import { ScrollArea } from '@easyflow/ui-frontend/components/ui/scroll-area';
import { PA_SKILL_NAME_REGEX } from '@easyflow/shared';
import type {
  PaAdminSkill,
  PaConversationSkillState,
  PaSkill,
  PaSkillCreateRequest,
  PaSkillListResponse,
  PaConversationSkillsResponse,
  PaSkillUpdateRequest,
} from '@easyflow/shared';
import { apiClient } from '@/lib/api';

// Task #3821 — Sezione Skills della sidebar PA (playbook di procedure).
// UNA sola lista: skill personali/condivise dell'utente (editabili) + Claude
// Skills assegnate dall'admin in SOLA LETTURA quando l'agente gira su Claude.
// Confine skill ≠ memoria: qui vivono procedure esplicite versionate, mai
// voci di memoria implicita (quelle stanno nel pannello Memoria).

interface Props {
  assistantId: string;
  /** Conversazione corrente: abilita i toggle attiva/disattiva per-conversazione. */
  currentConversationId: string | null;
}

interface DraftForm {
  name: string;
  description: string;
  content: string;
  visibility: 'personal' | 'shared';
}

const EMPTY_DRAFT: DraftForm = { name: '', description: '', content: '', visibility: 'personal' };

export const SkillsPanel: React.FC<Props> = ({ assistantId, currentConversationId }) => {
  const [skills, setSkills] = useState<PaSkill[]>([]);
  const [adminSkills, setAdminSkills] = useState<PaAdminSkill[]>([]);
  const [convStates, setConvStates] = useState<PaConversationSkillState[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | 'new' | null>(null);
  const [draft, setDraft] = useState<DraftForm>(EMPTY_DRAFT);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiClient.get<PaSkillListResponse>(
        `/personal-assistant/workspace/skills?assistantId=${encodeURIComponent(assistantId)}`,
      );
      setSkills(res.data?.skills ?? []);
      setAdminSkills(res.data?.adminSkills ?? []);
    } catch (err) {
      console.warn('[SkillsPanel] load failed', err);
      setError('Caricamento skill non riuscito');
    } finally {
      setLoading(false);
    }
  }, [assistantId]);

  const refreshConvStates = useCallback(async () => {
    if (!currentConversationId) { setConvStates([]); return; }
    try {
      const res = await apiClient.get<PaConversationSkillsResponse>(
        `/personal-assistant/workspace/skills/conversations/${currentConversationId}`,
      );
      setConvStates(res.data?.states ?? []);
    } catch (err) {
      console.warn('[SkillsPanel] conv states load failed', err);
    }
  }, [currentConversationId]);

  useEffect(() => { void refresh(); }, [refresh]);
  useEffect(() => { void refreshConvStates(); }, [refreshConvStates]);

  const convStateFor = useMemo(() => {
    const map = new Map<string, PaConversationSkillState>();
    for (const s of convStates) map.set(s.skillId, s);
    return map;
  }, [convStates]);

  const startEdit = (s: PaSkill) => {
    setEditingId(s.id);
    setDraft({ name: s.name, description: s.description, content: s.content, visibility: s.visibility });
  };

  const startNew = () => {
    setEditingId('new');
    setDraft(EMPTY_DRAFT);
  };

  const cancelEdit = () => { setEditingId(null); setDraft(EMPTY_DRAFT); };

  const draftValid = PA_SKILL_NAME_REGEX.test(draft.name) && draft.description.trim().length > 0 && draft.content.trim().length > 0;

  const submitDraft = async () => {
    if (!draftValid || saving) return;
    setSaving(true);
    setError(null);
    try {
      if (editingId === 'new') {
        const body: PaSkillCreateRequest = {
          name: draft.name,
          description: draft.description.trim(),
          content: draft.content,
          visibility: draft.visibility,
          status: 'active',
        };
        await apiClient.post(`/personal-assistant/workspace/skills`, body);
      } else if (editingId) {
        const body: PaSkillUpdateRequest = {
          name: draft.name,
          description: draft.description.trim(),
          content: draft.content,
          visibility: draft.visibility,
        };
        await apiClient.put(`/personal-assistant/workspace/skills/${editingId}`, body);
      }
      cancelEdit();
      await refresh();
    } catch (err) {
      console.warn('[SkillsPanel] save failed', err);
      setError(err instanceof Error ? err.message : 'Salvataggio non riuscito');
    } finally {
      setSaving(false);
    }
  };

  const approveOne = async (id: string) => {
    setBusyId(id);
    try {
      await apiClient.post(`/personal-assistant/workspace/skills/${id}/approve`, {});
      await refresh();
    } catch (err) {
      console.warn('[SkillsPanel] approve failed', err);
      setError(err instanceof Error ? err.message : 'Approvazione non riuscita');
    } finally {
      setBusyId(null);
    }
  };

  const promoteOne = async (id: string) => {
    setBusyId(id);
    setError(null);
    try {
      await apiClient.post(`/personal-assistant/workspace/skills/${id}/promote`, {});
      await refresh();
    } catch (err) {
      console.warn('[SkillsPanel] promote failed', err);
      setError(err instanceof Error ? err.message : 'Promozione a Claude Skill non riuscita');
      await refresh(); // lo stato di sync error è persistito e visibile
    } finally {
      setBusyId(null);
    }
  };

  const removeOne = async (id: string) => {
    setBusyId(id);
    try {
      await apiClient.delete(`/personal-assistant/workspace/skills/${id}`);
      setSkills(prev => prev.filter(s => s.id !== id));
    } catch (err) {
      console.warn('[SkillsPanel] delete failed', err);
      setError(err instanceof Error ? err.message : 'Eliminazione non riuscita');
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmDelete = (id: string) => {
    if (confirmDeleteId === id) {
      setConfirmDeleteId(null);
      void removeOne(id);
    } else {
      setConfirmDeleteId(id);
      window.setTimeout(() => setConfirmDeleteId(prev => (prev === id ? null : prev)), 4000);
    }
  };

  const setConvState = async (skillId: string, state: 'active' | 'disabled' | 'inherit') => {
    if (!currentConversationId) return;
    try {
      await apiClient.put(
        `/personal-assistant/workspace/skills/conversations/${currentConversationId}/${skillId}`,
        { state },
      );
      await refreshConvStates();
    } catch (err) {
      console.warn('[SkillsPanel] conv state set failed', err);
    }
  };

  const renderEditor = () => (
    <div className="px-2 pb-2 space-y-1.5 border-b border-border">
      <input
        type="text"
        value={draft.name}
        onChange={(e) => setDraft(d => ({ ...d, name: e.target.value.toLowerCase() }))}
        placeholder="nome-skill (minuscole, - e _)"
        className="w-full text-xs border border-border rounded px-2 py-1 font-mono"
      />
      {draft.name.length > 0 && !PA_SKILL_NAME_REGEX.test(draft.name) && (
        <div className="text-[10px] text-red-600">Solo minuscole, cifre, - e _ (2-64 caratteri)</div>
      )}
      <input
        type="text"
        value={draft.description}
        onChange={(e) => setDraft(d => ({ ...d, description: e.target.value }))}
        placeholder="Quando va usata questa procedura?"
        className="w-full text-xs border border-border rounded px-2 py-1"
      />
      <textarea
        value={draft.content}
        onChange={(e) => setDraft(d => ({ ...d, content: e.target.value }))}
        placeholder={'Procedura in markdown…\n1. Primo passo\n2. Secondo passo'}
        rows={8}
        className="w-full text-xs border border-border rounded px-2 py-1 resize-y font-mono"
      />
      <select
        value={draft.visibility}
        onChange={(e) => setDraft(d => ({ ...d, visibility: e.target.value === 'shared' ? 'shared' : 'personal' }))}
        className="w-full text-xs border border-border rounded px-2 py-1"
      >
        <option value="personal">Personale (solo io)</option>
        <option value="shared">Condivisa con il team</option>
      </select>
      <div className="flex gap-1.5">
        <Button size="sm" className="flex-1 h-7 text-xs" onClick={submitDraft} disabled={!draftValid || saving}>
          {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : (editingId === 'new' ? 'Crea skill' : 'Salva modifiche')}
        </Button>
        <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={cancelEdit}>Annulla</Button>
      </div>
    </div>
  );

  const renderSyncBadge = (s: PaSkill) => {
    if (s.syncStatus === 'synced') {
      return (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 inline-flex items-center gap-0.5" title={`Sincronizzata come Claude Skill${s.syncedAt ? ` (${new Date(s.syncedAt).toLocaleString('it-IT')})` : ''}`}>
          <UploadCloud className="h-2.5 w-2.5" /> Claude
        </span>
      );
    }
    if (s.syncStatus === 'error') {
      return (
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-red-100 text-red-700 inline-flex items-center gap-0.5" title={`Errore sync: ${s.syncError ?? 'sconosciuto'} — la skill resta attiva via contesto`}>
          <AlertTriangle className="h-2.5 w-2.5" /> Sync fallita
        </span>
      );
    }
    return null;
  };

  const renderConvToggle = (s: PaSkill) => {
    if (!currentConversationId || s.status !== 'active') return null;
    const st = convStateFor.get(s.id);
    const isDisabled = st?.state === 'disabled';
    const isActive = st?.state === 'active';
    return (
      <div className="flex items-center gap-1 mt-1">
        <span className="text-[10px] text-muted-foreground">In questa chat:</span>
        <button
          type="button"
          onClick={() => setConvState(s.id, isActive ? 'inherit' : 'active')}
          className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-0.5 ${isActive ? 'bg-violet-600 text-white border-violet-600' : 'bg-card border-border text-muted-foreground hover:bg-muted'}`}
          title={isActive ? 'Attiva in questa conversazione (clic per tornare al default)' : 'Attiva sempre in questa conversazione'}
        >
          <Check className="h-2.5 w-2.5" /> Attiva
        </button>
        <button
          type="button"
          onClick={() => setConvState(s.id, isDisabled ? 'inherit' : 'disabled')}
          className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-0.5 ${isDisabled ? 'bg-gray-800 text-white border-gray-800' : 'bg-card border-border text-muted-foreground hover:bg-muted'}`}
          title={isDisabled ? 'Disattivata in questa conversazione (clic per tornare al default)' : 'Disattiva in questa conversazione (mai auto-attivata)'}
        >
          <Ban className="h-2.5 w-2.5" /> Off
        </button>
        {(isActive || isDisabled) && (
          <button
            type="button"
            onClick={() => setConvState(s.id, 'inherit')}
            className="text-[10px] px-1 py-0.5 rounded text-muted-foreground hover:bg-muted inline-flex items-center"
            title="Torna al comportamento di default"
          >
            <RotateCcw className="h-2.5 w-2.5" />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-2 py-2">
        <span className="text-[11px] font-semibold uppercase text-muted-foreground inline-flex items-center gap-1">
          <BookOpen className="h-3 w-3" /> Skills (procedure)
        </span>
        <Button variant="ghost" size="sm" className="h-6 px-1.5 text-xs" onClick={() => (editingId === 'new' ? cancelEdit() : startNew())}>
          <Plus className="h-3 w-3 mr-0.5" /> {editingId === 'new' ? 'Annulla' : 'Nuova'}
        </Button>
      </div>

      {error && (
        <div className="mx-2 mb-1.5 px-2 py-1 rounded bg-red-50 border border-red-200 text-[11px] text-red-700 flex items-center gap-1">
          <AlertTriangle className="h-3 w-3 shrink-0" /> <span className="flex-1">{error}</span>
          <button type="button" onClick={() => setError(null)}><X className="h-3 w-3" /></button>
        </div>
      )}

      {editingId === 'new' && renderEditor()}

      <ScrollArea className="flex-1">
        {loading ? (
          <div className="flex items-center justify-center py-6 text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
          </div>
        ) : (
          <div className="px-2 pb-2 space-y-1">
            {skills.length === 0 && adminSkills.length === 0 && (
              <div className="text-xs text-muted-foreground py-4 text-center">
                Nessuna skill. Creane una qui o chiedi al PA in chat di imparare una procedura.
              </div>
            )}

            {skills.map(s => (
              <div key={s.id} className="rounded border border-border bg-card p-2">
                {editingId === s.id ? renderEditor() : (
                  <>
                    <div className="flex items-center gap-1 flex-wrap">
                      <span className="text-[11px] font-mono font-medium text-foreground truncate" title={`/skill ${s.name}`}>/{s.name}</span>
                      {s.status === 'draft' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Bozza</span>
                      )}
                      {s.visibility === 'shared' && (
                        <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-100 text-blue-700 inline-flex items-center gap-0.5"><Users className="h-2.5 w-2.5" /> Team</span>
                      )}
                      {renderSyncBadge(s)}
                      <span className="text-[10px] text-muted-foreground ml-auto">v{s.version}</span>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-0.5 break-words">{s.description}</div>
                    {renderConvToggle(s)}
                    <div className="flex items-center gap-1 mt-1.5">
                      {s.status === 'draft' && s.isOwner && (
                        <Button size="sm" className="h-6 px-2 text-[10px] bg-emerald-600 hover:bg-emerald-700" onClick={() => approveOne(s.id)} disabled={busyId === s.id}>
                          {busyId === s.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Check className="h-3 w-3 mr-0.5" /> Approva</>}
                        </Button>
                      )}
                      {s.isOwner && (
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => startEdit(s)} title="Modifica">
                          <Pencil className="h-3 w-3" />
                        </Button>
                      )}
                      {s.isOwner && s.status === 'active' && s.syncStatus !== 'synced' && (
                        <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[10px]" onClick={() => promoteOne(s.id)} disabled={busyId === s.id} title="Promuovi a Claude Skill nativa (esecuzione nel container Anthropic)">
                          <Sparkles className="h-3 w-3 mr-0.5" /> Claude
                        </Button>
                      )}
                      {s.isOwner && (
                        <button
                          type="button"
                          onClick={() => handleConfirmDelete(s.id)}
                          className={`ml-auto h-6 px-1.5 rounded border text-[10px] inline-flex items-center gap-1 transition ${
                            confirmDeleteId === s.id
                              ? 'bg-red-600 text-white border-red-600 hover:bg-red-700'
                              : 'bg-card border-border text-muted-foreground hover:bg-red-50 hover:border-red-300 hover:text-red-600'
                          }`}
                          title={confirmDeleteId === s.id ? 'Conferma eliminazione' : 'Elimina'}
                        >
                          <Trash2 className="h-3 w-3" />
                          {confirmDeleteId === s.id && <span>Conferma</span>}
                        </button>
                      )}
                    </div>
                  </>
                )}
              </div>
            ))}

            {adminSkills.length > 0 && (
              <>
                <div className="text-[10px] font-semibold uppercase text-muted-foreground pt-2 pb-0.5 inline-flex items-center gap-1">
                  <Lock className="h-2.5 w-2.5" /> Gestite dall'amministratore
                </div>
                {adminSkills.map(a => (
                  <div key={a.id} className="rounded border border-dashed border-border bg-muted/50 p-2" title="Claude Skill assegnata dall'amministratore nella configurazione dell'agente: sola lettura">
                    <div className="flex items-center gap-1">
                      <span className="text-[11px] font-medium text-foreground truncate">{a.displayTitle}</span>
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-muted text-muted-foreground inline-flex items-center gap-0.5 ml-auto">
                        <Lock className="h-2.5 w-2.5" /> admin
                      </span>
                    </div>
                    {a.description && <div className="text-[11px] text-muted-foreground mt-0.5 break-words">{a.description}</div>}
                  </div>
                ))}
              </>
            )}
          </div>
        )}
      </ScrollArea>
    </div>
  );
};
